import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LINEN_ITEMS } from "@/lib/linenItems";
import { tx } from "@/i18n";

const toForm = (p) => Object.fromEntries(Object.entries(p || {}).map(([k, v]) => [k, String(v).replace(".", ",")]));

export const LinenPricesDialog = ({ open, onOpenChange, houses, onSaved }) => {
  const [scope, setScope] = useState("default");
  const [defaults, setDefaults] = useState({});
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/linen/prices", { params: scope === "default" ? {} : { house_id: scope } })
      .then(({ data }) => { setDefaults(data.defaults); setForm(toForm(scope === "default" ? data.defaults : data.house)); })
      .catch((e) => toast.error(apiError(e)));
  }, [open, scope]);

  const save = async () => {
    setBusy(true);
    try {
      const url = scope === "default" ? "/linen/prices/default" : `/linen/prices/house/${scope}`;
      await api.put(url, { prices: form });
      toast.success(tx("Linnenprijzen opgeslagen"));
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="linen-prices-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{tx("Linnenprijzen")}</DialogTitle>
          <DialogDescription>{tx("Prijs per stuk, exclusief btw. Een woningprijs gaat voor de standaardprijs; laat leeg om de standaard te gebruiken.")}</DialogDescription>
        </DialogHeader>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="linen-prices-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">{tx("Algemene standaard")}</SelectItem>
            {houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {LINEN_ITEMS.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <span className="flex-1 truncate" title={label}>{label}</span>
              <span className="relative w-28 shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                <Input inputMode="decimal" value={form[key] || ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  placeholder={scope !== "default" && defaults[key] != null ? String(defaults[key]).replace(".", ",") : "0,00"}
                  className="h-8 pl-5 text-right" data-testid={`linen-price-${key}`} />
              </span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{tx("Sluiten")}</Button>
          <Button onClick={save} disabled={busy} className="bg-primary hover:bg-primary/90 gap-2" data-testid="linen-prices-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {tx("Opslaan")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
